import '../app/marketing/marketing.css';
import LeadForm from '../components/LeadForm';





import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VideoCamera, ChatCircleText, FilmSlate, FilmReel, Cards, BookOpen, Lightning, ForkKnife, Newspaper, MagicWand, MagnifyingGlass, Target, Palette, Images, UsersThree, Funnel, PenNib, TrendUp, Megaphone, ChartLineUp, ShieldCheck, CheckCircle, UserCircle, MapPin, Translate, Cube, House, Flask, Browser, TreeStructure, Globe } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { trackEvent } from '../lib/analytics';



function sortVideosNaturally(files) {
  if (!Array.isArray(files)) return [];
  const parseKey = (filename) => {
    const base = String(filename).replace(/\.[^.]+$/, '');
    return base.split(/[-_]/).map((p) => {
      const n = parseInt(p, 10);
      return isNaN(n) ? p : n;
    });
  };

  const compareKeys = (a, b) => {
    const ka = parseKey(a);
    const kb = parseKey(b);
    const max = Math.max(ka.length, kb.length);
    for (let i = 0; i < max; i++) {
      const va = ka[i];
      const vb = kb[i];
      if (va === undefined) return -1;
      if (vb === undefined) return 1;
      if (typeof va === 'number' && typeof vb === 'number') {
        if (va !== vb) return va - vb;
      } else {
        const cmp = String(va).localeCompare(String(vb));
        if (cmp !== 0) return cmp;
      }
    }
    return String(a).localeCompare(String(b));
  };

  const newAdditions = [];
  const standardFiles = [];

  for (const f of files) {
    const base = String(f).replace(/\.[^.]+$/, '');
    if (base.includes('-') || base.includes('_')) {
      newAdditions.push(f);
    } else {
      standardFiles.push(f);
    }
  }

  return [...newAdditions, ...standardFiles];
}

const faqData = [
  {
    q: "How does the AI Marketing Agent enforce our brand guidelines?",
    a: "You build your Brand Kit once by uploading your palette, logo variants, and design rules or pulling them from a URL. Every studio automatically binds these design tokens so text, colors, and motion match your exact brand identity."
  },
  {
    q: "Are the generated video assets and avatars rights-cleared for commercial ads?",
    a: "Yes, 100%. All avatars, music tracks, and motion graphic assets are licensed for commercial use across Meta, TikTok, YouTube, LinkedIn, and TV campaigns with no copyright risk."
  },
  {
    q: "What do we actually get in the first 48 hours?",
    a: "Your complete Brand Kit, five finished video or static ad concepts, and an initial AEO website audit — all within the first 48 hours. We confirm the scope with you before we start so the turnaround is realistic for your brand."
  },
  {
    q: "Can we integrate the output directly into WordPress and Brevo?",
    a: "Yes. Growth Studio includes native WordPress + IndexNow publishing for instant GEO indexing and Brevo campaign execution for email lifecycle automation."
  }
];

function MarketingFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => {
    const nextIdx = openIndex === idx ? null : idx;
    setOpenIndex(nextIdx);
    if (nextIdx !== null) {
      trackEvent('marketing_faq_expanded', { question: faqData[idx].q });
    }
  };

  return (
    <div style={{ maxWidth: '850px', margin: '0 auto' }}>
      {faqData.map((item, idx) => (
        <div key={idx} style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', overflow: 'hidden', transition: 'all 0.2s' }}>
          <button
            onClick={() => toggle(idx)}
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', outline: 'none' }}
          >
            <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', paddingRight: '16px' }}>{item.q}</span>
            <span style={{ color: '#8b5cf6', fontWeight: 800, fontSize: '1.4rem' }}>{openIndex === idx ? '−' : '+'}</span>
          </button>
          {openIndex === idx && (
            <div style={{ padding: '0 24px 24px 24px', color: '#475569', fontSize: '1.05rem', lineHeight: 1.7, borderTop: '1px solid #f1f5f9' }}>
              <p style={{ marginTop: '16px', margin: 0 }}>{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [videoFiles, setVideoFiles] = useState([]);

  useEffect(() => {
    fetch('/videos-manifest.json?v=' + Date.now())
      .then((r) => r.json())
      .then((files) => setVideoFiles(sortVideosNaturally(files)))
      .catch(() => setVideoFiles([]));
  }, []);

  const studiosData = [
    {
      title: "Motion Ad Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "Describe an ad in plain words. Get a studio-grade, style-locked storyboard (PRD best-practice). Bind cast from the avatar library per beat, edit lines, approve, and render multi-shot cinematic video.",
      tags: ["Style-locked storyboard", "Per-beat cast + dialogue", "Cinematic, multi-vertical"],
      icon: <VideoCamera size={24} color="currentColor" />
    },
    {
      title: "Explainer Ads",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "Narrated explainers for cold audiences — open on a question people actually wonder about, build the answer over b-roll, land the brand as the natural conclusion. Voiceover-led, captions on, no talking heads required.",
      tags: ["TOF cold traffic", "Journalist voice, not salesy", "Works with your saved Look"],
      icon: <ChatCircleText size={24} color="currentColor" />
    },
    {
      title: "Motion Graphics Film",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "Storyboard a style-locked animated explainer (editorial collage, kinetic type, infographic, clay, blueprint…). Lock ONE look, then every beat changes only the action — legible type/data render pixel-exact (code), stylized scenes render as AI motion, one narration spine over the whole cut.",
      tags: ["7 locked style families", "Engine-routed beats", "No AI-garbled text"],
      icon: <FilmSlate size={24} color="currentColor" />
    },
    {
      title: "Video Recreation Kit",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "Drop a brand video you own. It's split into ~5s segments, each read by Gemini, rebuilt in Remotion, and graded against the original — refining until it matches, then stitched into one MP4 you can restyle. For recreating your own videos into reusable templates, not cloning others'.",
      tags: ["Gemini reads the motion", "Graded to match", "Rights-gated"],
      icon: <FilmReel size={24} color="currentColor" />
    },
    {
      title: "Comic Strip Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "creative",
      description: "Turn a topic into a multi-panel comic strip — a consistent cast in one style across every panel, with crisp typeset speech bubbles, title and tagline. The model draws the art; the text is always legible (no garbled AI captions). One download-ready PNG.",
      tags: ["Consistent cast", "Crisp typeset bubbles", "Any language"],
      icon: <Cards size={24} color="currentColor" />
    },
    {
      title: "AI Storytime",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "First-person storytime animation the way the top creators build it: study the hook → escalation → payoff structure, lock ONE recurring 2D character, break the script into scenes, then generate character-consistent shots, animate them and narrate — one connected story, not a bag of AI clips.",
      tags: ["Locked recurring character", "Hook → escalation → payoff", "Scene-consistent + narrated"],
      icon: <BookOpen size={24} color="currentColor" />
    },
    {
      title: "Content Carousel",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Paste a blog post, release notes or any article — it becomes a swipeable, on-brand LinkedIn/IG carousel. Every slide is code-rendered (legible headlines, stats, comparisons, diagrams, a CTA close), grounded in the source, with your logo + palette. No AI-photo text gibberish.",
      tags: ["URL / markdown → deck", "Code-exact legible slides", "Brand kit + sessions"],
      icon: <Cards size={24} color="currentColor" />
    },
    {
      title: "Template Ads",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Code-rendered templated video — plan scenes from a one-line brief, edit the text and prices, render in seconds. Numbers are always pixel-exact (no AI-garbled text), and identical plans never re-bill. Built for promo and retargeting volume.",
      tags: ["Pixel-exact prices", "Seconds, not minutes", "BOF promo volume"],
      icon: <Lightning size={24} color="currentColor" />
    },
    {
      title: "Menu Promo",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Upload (or paste-and-label) dish photos and get a VideoHive-class animated promo — the food is auto-cut-out to float over kinetic 3D type, ghost background lettering and particle dust. Built for restaurants, cafés and food brands.",
      tags: ["Auto food cutout", "Kinetic 3D title", "Any dish, any brand"],
      icon: <ForkKnife size={24} color="currentColor" />
    },
    {
      title: "Menu Visualizer",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Snap a photo of any restaurant menu and get an appetising food shot plus a plain 'what is this' line for every dish — so a diner instantly sees what a Tagine or Cavatappi actually is. Ships a QR to the live menu and a downloadable standalone web page.",
      tags: ["Reads the whole menu", "QR to live page", "Downloadable site (.zip)"],
      icon: <ForkKnife size={24} color="currentColor" />
    },
    {
      title: "Moment Studio",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Scans this week's interesting moments — non-political, no names, no brands — and abstracts each to a universal tension plus a single striking visual, in the spirit of Nike's \"White is the rule. One exception.\" Get concepts + rendered hero images, and schedule the digest to your inbox daily.",
      tags: ["Brand-safe by design", "Concept + hero image", "Daily email digest"],
      icon: <Newspaper size={24} color="currentColor" />
    },
    {
      title: "Research-Driven Ad Studio",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "intelligence",
      description: "Drop a product URL or story, research what competitors run on Meta & TikTok, and generate a research-grounded creative plan you can edit before producing images or video.",
      tags: ["URL-first", "Live competitor research", "Editable plan"],
      icon: <MagicWand size={24} color="currentColor" />
    },
    {
      title: "Social Audit",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "intelligence",
      description: "Paste any brand's website — discover their social channels, then run a 30-day Teaser (Social Health score + 3 hooks, fixes locked — the lead magnet) or the Full 90-day report (all findings + recommendations + playbook).",
      tags: ["Website → channels", "Teaser or Full", "Score + hooks + playbook"],
      icon: <MagnifyingGlass size={24} color="currentColor" />
    },
    {
      title: "Competitor Swipe & Adapt",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "intelligence",
      description: "Name your competitors, mine their top organic Reels/TikToks, tear down why each one wins, then adapt the winner into your own branded ad concepts.",
      tags: ["IG + TikTok", "Why-it-works teardown", "Swipe & adapt"],
      icon: <MagnifyingGlass size={24} color="currentColor" />
    },
    {
      title: "Competitor Intelligence",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "intelligence",
      description: "Watch competitors and get alerts when they publish, plus posting cadence/momentum, voice-of-customer wedges, and offer/promo breakdowns — all the captured intel in one dashboard.",
      tags: ["Weekly watch + alerts", "Cadence + momentum", "VoC + offers"],
      icon: <Target size={24} color="currentColor" />
    },
    {
      title: "Brand Kit",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "brand",
      description: "Build a brand identity once — generate from a prompt (palette, type, voice, logo + variants), learn best practices from competitors, or extract from a URL. Saves an enterprise-grade kit with design tokens that every other studio selects.",
      tags: ["Design tokens", "Logo variants", "Competitor learning"],
      icon: <Palette size={24} color="currentColor" />
    },
    {
      title: "Brand Looks",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "brand",
      description: "Train the brand look once — upload creative you love and we distill it into an editable look card (palette, light, grade, set). Pick it in any studio and every render, and every beat re-roll, comes back in that exact look.",
      tags: ["Vision-distilled look card", "Edit in plain English", "Locks style + scenes"],
      icon: <Palette size={24} color="currentColor" />
    },
    {
      title: "Finished Ad Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "creative",
      description: "Turn a product photo into a finished, on-brand ad — real headline, bullets, CTA, rating and badges over the scene, on a brand template. Agent drafts the copy, you edit, export every format.",
      tags: ["AI copy deck", "Brand templates", "1:1 · 4:5 · 9:16"],
      icon: <Images size={24} color="currentColor" />
    },
    {
      title: "Constant Creative Refresh",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "creative",
      description: "Batch-generate 10–50 paid-ad creatives per SKU — product-in-context images, short-form video, and avatar UGC across 15 background presets.",
      tags: ["10–50 / SKU", "Video + avatar", "Furniture catalog"],
      icon: <Images size={24} color="currentColor" />
    },
    {
      title: "UGC Ad Factory",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "video",
      description: "Turn a product URL into a 15–30s UGC-style ad: pick a format and avatar presenter, edit the beat-structured script, then generate the voiced vertical video with on-screen hooks.",
      tags: ["5 UGC formats", "Avatar presenter", "TikTok-ready 9:16"],
      icon: <VideoCamera size={24} color="currentColor" />
    },
    {
      title: "Production Studio",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "creative",
      description: "Build and lock the people in your ads — generate or pick characters, build character sheets, and restyle their wardrobe. Rights-clean, reusable across every ad. (Locations live in Motion Ad Studio.)",
      tags: ["Rights-clean", "Localized faces", "Reusable"],
      icon: <UsersThree size={24} color="currentColor" />
    },
    {
      title: "Carousel Studio",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "creative",
      description: "Generate a faceless content carousel — a niche slide-set (food, skincare, fitness) on one locked visual system so the whole post reads as one brand, with a relatable POV hook overlay and an optional subtle-loop on the hook slide. No people, no likeness risk.",
      tags: ["Faceless", "One locked template", "POV hooks"],
      icon: <Images size={24} color="currentColor" />
    },
    {
      title: "Lead Gen Studio",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "growth",
      description: "Run a pay-per-lead business: build a niche lead-capture funnel, publish it to get a live capture link, and manage the leads that come in. You own the asset.",
      tags: ["Niche funnels", "Lead capture", "Own the data"],
      icon: <Funnel size={24} color="currentColor" />
    },
    {
      title: "Visual Content Studio",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "creative",
      description: "Generate high-impact marketing imagery, ad creatives, and brand assets using state-of-the-art AI diffusion models.",
      tags: ["Diffusion models", "Brand assets", "Ad creatives"],
      icon: <MagicWand size={24} color="currentColor" />
    },
    {
      title: "Content Studio",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "brand",
      description: "The whole content factory — multiply one asset into every format, train brand voice, write for AI answer boxes, gate out generic. Not just UGC.",
      tags: ["Content Multiplier", "Brand voice", "AEO + quality gate"],
      icon: <PenNib size={24} color="currentColor" />
    },
    {
      title: "Growth Studio",
      colorHex: "#10b981",
      colorRgb: "16, 185, 129",
      category: "growth",
      description: "The organic-growth & lifecycle factory — publish to WordPress + IndexNow, draft research-locked GEO articles AI engines cite, run Brevo email campaigns, optimize listings for AI shopping, and track rankings with feedback loops.",
      tags: ["WordPress publish", "GEO + Brevo", "Rank tracking"],
      icon: <TrendUp size={24} color="currentColor" />
    },
    {
      title: "Website Audits",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "growth",
      description: "Manage audited companies: add a company, scan its site, pick pages to review, run AEO/GEO audits, track run history and compare before/after.",
      tags: ["Per-company", "Scan + select pages", "Before/after"],
      icon: <MagnifyingGlass size={24} color="currentColor" />,
      link: "/agents/aeo"
    },
    {
      title: "Lead-Gen Outreach",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "growth",
      description: "The acquisition engine: find product brands to pitch, run a free-teaser AEO audit of any URL, and email the branded teaser — send the free findings, sell the fixes.",
      tags: ["Prospect finder", "Free teaser PDF", "Brevo / mailto send"],
      icon: <Megaphone size={24} color="currentColor" />,
      link: "/agents/sdr-agent"
    },
    {
      title: "Market Research",
      colorHex: "#6366f1",
      colorRgb: "99, 102, 241",
      category: "intelligence",
      description: "Surface market insights, trends, and competitor positioning to inform campaign strategy.",
      tags: ["Trends", "Competitor", "Strategy"],
      icon: <ChartLineUp size={24} color="currentColor" />
    },
    {
      title: "Avatar Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "creative",
      description: "Build a reusable cast instead of a new stranger every render. Pick a style mode (creator-real, aspirational, expert, fan-culture), an audience tribe and a cultural context — Singapore, Korea, Japan, Malaysia, Indonesia, India — then lock wardrobe and face so the same person carries every beat and every campaign.",
      tags: ["Locked identity across beats", "Audience tribes + cultural cues", "Saved avatar library"],
      icon: <UserCircle size={24} color="currentColor" />
    },
    {
      title: "Location Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "creative",
      description: "Put the shot somewhere real. Stock backgrounds — kitchen, street, gym, cafe, studio — plus visual-localization markets so a scene reads as the country you are selling into. Every saved location is vision-checked on the actual pixels, not just the prompt that asked for it.",
      tags: ["Localized to market", "Vision-QA'd backgrounds", "Reusable location library"],
      icon: <MapPin size={24} color="currentColor" />
    },
    {
      title: "Landing Page Studio",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "growth",
      description: "The click has to land somewhere. Approve a visual direction first, then generate a complete self-contained responsive page built from real section blocks — hero, features, how-it-works, gallery, testimonial, CTA — for a lead or sale goal, and vision-QA the rendered result before it goes live.",
      tags: ["Art direction approved first", "Self-contained responsive page", "Vision QA before launch"],
      icon: <Browser size={24} color="currentColor" />
    },
    {
      title: "Dub & Localize",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "One winning ad, every market. Dub into target languages with subtitle modes, and transcreate the approved copy deck or template scene list rather than translating it literally — then verify fidelity by back-translating, so the claim that survives is the claim you signed off.",
      tags: ["Transcreation, not translation", "Back-translation fidelity check", "Copy decks + template scenes"],
      icon: <Translate size={24} color="currentColor" />
    },
    {
      title: "Experiment & Post-Mortem",
      colorHex: "#f59e0b",
      colorRgb: "245, 158, 11",
      category: "intelligence",
      description: "Stop guessing which change worked. Plans a structured test that validates only one variable moves and calculates the minimum sample size, diagnoses why CPM is climbing across the four real causes, and turns a concluded test into a post-mortem that extracts the winning hooks and angles.",
      tags: ["One-variable validation", "Minimum sample size", "CPM cause diagnosis"],
      icon: <Flask size={24} color="currentColor" />
    },
    {
      title: "Pre-Launch QA",
      colorHex: "#f59e0b",
      colorRgb: "245, 158, 11",
      category: "intelligence",
      description: "Three gates before spend. Accessibility scoring that goes past captions, platform-policy compliance on hooks, primary text, overlays and CTA — health claims, before/after, restricted categories — and a distinctiveness score that checks your brand assets are actually present.",
      tags: ["Policy check before spend", "Accessibility scored", "Brand distinctiveness (DBA)"],
      icon: <ShieldCheck size={24} color="currentColor" />
    },
    {
      title: "Miniature World",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "creative",
      description: "The scroll-stopping miniature format — tiny worlds built on and around your product, with character archetypes and the giant intervention hand that reaches in. A distinct visual language for feeds where every other ad looks like every other ad.",
      tags: ["Miniature style families", "Character archetypes", "Intervention-hand shots"],
      icon: <Cube size={24} color="currentColor" />
    },
    {
      title: "Listing Video Studio",
      colorHex: "#8b5cf6",
      colorRgb: "139, 92, 246",
      category: "video",
      description: "Property photos in, one cut listing video out. Each photo is classified by room, ordered into a walkthrough that makes spatial sense, and assembled into a single video — built for property and marketplace listings that arrive as a folder of stills.",
      tags: ["Room classification", "Ordered walkthrough", "Photos → one cut"],
      icon: <House size={24} color="currentColor" />
    },
    {
      title: "Diagram Studio",
      colorHex: "#d946ef",
      colorRgb: "217, 70, 239",
      category: "creative",
      description: "Explain the thing without hiring a designer. Renders diagrams in locked style families — soft-tech, flat-vector, blueprint — where the labels are code-rendered and legible, so a technical concept survives contact with a social feed.",
      tags: ["Locked style families", "Legible code-rendered labels", "Technical concepts"],
      icon: <TreeStructure size={24} color="currentColor" />
    },
    {
      title: "WordPress Publishing",
      colorHex: "#10b981",
      colorRgb: "16, 185, 129",
      category: "growth",
      description: "Close the loop from draft to published. Create, update and retrieve posts and pages on your live WordPress site by ID or slug, and archive the current live content before anything changes — so a rewrite is always reversible.",
      tags: ["Publish direct to WordPress", "Snapshot before overwrite", "Posts + pages by slug"],
      icon: <Globe size={24} color="currentColor" />
    },
];
  
  const [lightboxId, setLightboxId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxId(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    document.body.classList.add('marketing-page');
    return () => document.body.classList.remove('marketing-page');
  }, []);

  return (
    <main className="marketing-theme-root" style={{ position: 'relative', zIndex: 1, backgroundColor: '#fff', minHeight: '100vh' }}>
      {/* Hero Section - Matching Main Page Hero */}
      <section className="hero" id="home">
        <div className="hero-video-bg">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="hero-video"
            poster="https://eagentix.com/assets/platform.png"
          >
            <source
              src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260217_030345_246c0224-10a4-422c-b324-070b7c0eceda.mp4"
              type="video/mp4"
            />
            <source
              src="https://eagentix.com/assets/hero-bg.mov"
              type="video/mp4"
            />
          </video>
          <div className="hero-overlay"></div>
        </div>

        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            MARKETING AGENT IS LIVE!
          </div>

          <h1 className="hero-title animate-fade-in-up">
            The Ultimate <span className="hero-title-gradient">AI Studio</span><br />
            for Growth Teams.
          </h1>

          <p className="hero-description animate-fade-in-up delay-100">
            Deploy autonomous agents to build your Brand Kit, generate UGC and Paid Ads, run Website Audits, and scale programmatic SEO content. Your entire marketing factory, ready in minutes.
          </p>

          <div className="hero-cta-group animate-fade-in-up delay-200">
            <a href="#contact" onClick={(e) => { e.preventDefault(); trackEvent('marketing_hero_start_building_click'); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn btn-primary">
              Start Building
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </a>
            <a href="#studios" onClick={(e) => { e.preventDefault(); trackEvent('marketing_hero_view_studios_click'); document.getElementById('studios')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn btn-ghost">
              View All Studios
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </a>
          </div>

          <div style={{ marginTop: '24px', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>
            ✓ 39 Studios • Brand-locked output • Scope agreed before we start
          </div>
        </div>
      </section>

      {/* Confidentiality note — client names withheld under NDA */}
      <section style={{ background: '#020617', padding: '0 0 64px 0' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ marginTop: '-60px', position: 'relative', zIndex: 10, background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '28px 32px', backdropFilter: 'blur(12px)', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.5)', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <ShieldCheck size={26} weight="duotone" color="#8b5cf6" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ color: '#cbd5e1', fontSize: '1rem', lineHeight: 1.7, margin: 0 }}>
                We work under NDA, so client names and campaign results stay confidential — the same
                confidentiality that would cover your own work. If you want a reference, we will arrange
                a conversation directly with a current client rather than publish a quote.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Video Showcase Section */}
      <section id="showcase" className="section use-cases-section" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="section-header" style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', textAlign: 'left', maxWidth: '100%' }}>
            <div>
              <h2 style={{ marginBottom: '0.5rem', color: '#1d1d1f', fontWeight: 800 }}>Video Showcase</h2>
              <p style={{ maxWidth: '600px', color: '#6e6e73' }}>Watch the AI Marketing Agent in action—generating stunning UGC, Explainer Ads, and Motion Ads directly from URLs.</p>
            </div>
          </div>
        </div>

        {/* Video Showcase grid that wraps into multiple rows */}
        <div style={{ position: 'relative', marginTop: '2rem' }}>
          <div 
            style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '1.5rem', 
              paddingBottom: '2rem', 
              paddingTop: '1rem',
            }}
          >
            {sortVideosNaturally(videoFiles).map((file) => {
              const isImage = file.match(/\.(png|jpe?g|gif|webp)$/i);
              return (
                <div 
                  key={file} 
                  className="glass-card video-card" 
                  style={{ flex: '0 0 auto', width: '320px', aspectRatio: '9/16', position: 'relative', padding: 0, overflow: 'hidden', background: '#000', borderRadius: '16px', cursor: 'pointer', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}
                  onClick={() => setLightboxId(file)}
                >
                  {isImage ? (
                    <img
                      src={`/videos/${file}`}
                      alt={file}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <video
                      src={`/videos/${file}`}
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Studios Section — Modern Categorized Layout */}
      <section id="studios" className="section features-section" style={{ backgroundColor: '#f5f5f7', position: 'relative', overflow: 'hidden' }}>
        {/* Background ambient glow */}
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translate(-50%, -50%)', width: '900px', height: '900px', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

        <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: '1320px' }}>
          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={{ display: 'inline-block', padding: '0.4rem 1.2rem', background: 'rgba(0,113,227,0.08)', border: '1px solid rgba(0,113,227,0.2)', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '600', color: '#0071e3', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
              {studiosData.length} Studios
            </span>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', fontWeight: '800', letterSpacing: '-1.5px', marginBottom: '1rem', color: '#1d1d1f' }}>
              Fully Autonomous Studios
            </h2>
            <p style={{ color: '#6e6e73', fontSize: '1.15rem', maxWidth: '600px', margin: '0 auto', lineHeight: '1.7' }}>
              Every creative and strategic marketing workflow — handled end-to-end by AI.
            </p>
          </div>

          {/* Category filter pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', marginBottom: '3rem' }}>
            {(() => {
              const categories = [
                { key: 'all', label: 'All Studios' },
                { key: 'video', label: 'Video & Motion' },
                { key: 'creative', label: 'Creative & Design' },
                { key: 'intelligence', label: 'Intelligence' },
                { key: 'brand', label: 'Brand & Content' },
                { key: 'growth', label: 'Growth & Outreach' },
              ];
              return categories.map(cat => {
                const isActive = activeCategory === cat.key;
                const count = cat.key === 'all' ? studiosData.length : studiosData.filter(s => s.category === cat.key).length;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    style={{
                      padding: '0.55rem 1.3rem',
                      borderRadius: '100px',
                      border: isActive ? '1px solid #0071e3' : '1px solid rgba(0,0,0,0.1)',
                      background: isActive ? 'rgba(0,113,227,0.08)' : '#fff',
                      color: isActive ? '#0071e3' : '#6e6e73',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {cat.label}
                    <span style={{
                      background: isActive ? 'rgba(0,113,227,0.12)' : 'rgba(0,0,0,0.05)',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '100px',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      color: isActive ? '#0071e3' : '#6e6e73',
                    }}>
                      {count}
                    </span>
                  </button>
                );
              });
            })()}
          </div>

          {/* Studio cards grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '1.25rem',
          }}>
            {studiosData
              .filter(s => activeCategory === 'all' || s.category === activeCategory)
              .map((studio, index) => (
              <div
                key={studio.title}
                onClick={() => studio.link && navigate(studio.link)}
                style={{
                  position: 'relative',
                  borderRadius: '20px',
                  padding: '1px',
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.07)',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                  transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                  opacity: 0,
                  animation: `fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.04}s forwards`,
                  cursor: studio.link ? 'pointer' : 'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)';
                }}
              >
                {/* Inner card */}
                <div style={{
                  background: '#fff',
                  borderRadius: '19px',
                  padding: '1.75rem',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {/* Icon + Title row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${studio.colorHex || '#8b5cf6'}, rgba(${studio.colorRgb || '139,92,246'}, 0.5))`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: `0 4px 12px rgba(${studio.colorRgb || '139,92,246'}, 0.25)`,
                    }}>
                      <span style={{ color: '#fff', display: 'flex' }}>{studio.icon}</span>
                    </div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      color: '#1d1d1f',
                      fontWeight: '650',
                      fontFamily: 'var(--font-display)',
                      letterSpacing: '-0.02em',
                      lineHeight: '1.25',
                    }}>{studio.title}</h3>
                  </div>

                  {/* Description */}
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#6e6e73',
                    lineHeight: '1.6',
                    margin: '0 0 auto 0',
                    flex: 1,
                  }}>{studio.description}</p>

                  {/* Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    {studio.tags.map((tag, i) => (
                      <span
                        key={i}
                        style={{
                          padding: '0.25rem 0.7rem',
                          background: `rgba(${studio.colorRgb || '0,113,227'}, 0.08)`,
                          borderRadius: '6px',
                          fontSize: '0.7rem',
                          color: `rgba(${studio.colorRgb || '0,113,227'}, 1)`,
                          fontWeight: '550',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {studio.link && (
                    <div style={{ marginTop: '0.85rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0071e3', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        Explore Agent &rarr;
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Tools Section */}
      <section id="tools" className="section use-cases-section" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="section-header">
            <h2 style={{ color: '#1d1d1f', fontWeight: 800 }}>Production Tools</h2>
            <p style={{ color: '#6e6e73' }}>Enterprise-grade utilities to refine and distribute your AI creatives globally.</p>
          </div>
          
          <div className="grid-3">
            <div className="glass-card" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <span className="tag" style={{ background: 'rgba(0,113,227,0.08)', color: '#0071e3' }}>Dub Studio</span>
              <h3 style={{ color: '#1d1d1f', marginTop: '1rem' }}>Global Translation</h3>
              <p style={{ marginTop: '0.5rem', color: '#6e6e73', lineHeight: '1.5' }}>Turn one ad into 18 languages. The voiceover is re-spoken in the target language with perfectly timed burned-in subtitles for paid social.</p>
            </div>
            
            <div className="glass-card" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <span className="tag" style={{ background: 'rgba(0,113,227,0.08)', color: '#0071e3' }}>Media Binder</span>
              <h3 style={{ color: '#1d1d1f', marginTop: '1rem' }}>Format Generation</h3>
              <p style={{ marginTop: '0.5rem', color: '#6e6e73', lineHeight: '1.5' }}>Instantly bind images into slideshow videos and export perfectly cropped aspects (1:1, 4:5, 9:16, 16:9) as MP4, GIF, or WebM.</p>
            </div>

            <div className="glass-card" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <span className="tag" style={{ background: 'rgba(0,113,227,0.08)', color: '#0071e3' }}>Caption Tool</span>
              <h3 style={{ color: '#1d1d1f', marginTop: '1rem' }}>Auto-Transcription</h3>
              <p style={{ marginTop: '0.5rem', color: '#6e6e73', lineHeight: '1.5' }}>Upload any video to get perfectly timed, styled captions burned in (TikTok bold, minimal, or boxed) so your ads win on muted feeds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What the first 48 hours produce */}
      <section style={{ background: '#0f172a', padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="container" style={{ maxWidth: '850px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)', 
            borderRadius: '24px', 
            padding: '48px', 
            border: '2px solid #8b5cf6', 
            boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.25)', 
            textAlign: 'center' 
          }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(139, 92, 246, 0.15)', border: '1px solid #8b5cf6', marginBottom: '20px' }}>
              <ShieldCheck size={36} color="#8b5cf6" />
            </div>
            <span style={{ color: '#c084fc', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              What The First 48 Hours Produce
            </span>
            <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', marginBottom: '16px' }}>
              Production-ready ad and content assets in 48 hours.
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto 28px' }}>
              Within 48 hours you have your enterprise Brand Kit, five cinematic ad concepts and an initial AEO website audit. We confirm the scope with you before we start, so the turnaround is realistic for your brand.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={20} color="#34d399" weight="fill" /> Scope confirmed before we start
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={20} color="#c084fc" weight="fill" /> Rights-Clean Commercial Assets
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualification Filter Section (§3.9) */}
      <section style={{ background: '#f8fafc', padding: '80px 0', borderTop: '1px solid #e2e8f0' }}>
        <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#8b5cf6', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              Qualification Filter
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
              Is the AI Marketing Agent Right for Your Team?
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', maxWidth: '950px', margin: '0 auto' }}>
            {/* IS FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '2px solid #10b981', boxShadow: '0 10px 30px rgba(16, 185, 129, 0.08)' }}>
              <div style={{ color: '#10b981', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                Who This IS For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#334155', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  <strong>Growth teams & DTC brands</strong> looking for high-volume UGC, Motion Ads, and fast iteration.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Brands looking to automate <strong>SEO/AEO search citations</strong> and LinkedIn/IG carousel production.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Marketers who want <strong>rights-clean commercial video assets</strong> generated in minutes.
                </li>
              </ul>
            </div>

            {/* IS NOT FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
              <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                Who This Is NOT For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#64748b', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Businesses expecting traditional slow agency retainers with manual 3-week revision cycles.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Teams looking for unverified low-quality AI image spam without brand kit design tokens.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Companies unwilling to lock a Brand Kit once for automated cross-studio consistency.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ background: '#ffffff', padding: '80px 0', borderTop: '1px solid #e2e8f0' }} id="faq">
        <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ color: '#8b5cf6', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              Frequently Asked Questions
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
              Got Questions? We Have Answers.
            </h2>
          </div>
          <MarketingFAQ />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: '#f5f5f7' }}>
        <div className="container" style={{ display: 'flex', flexWrap: 'wrap', gap: '4rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: '1 1 400px' }}>
            <h2 style={{ fontSize: '3rem', marginBottom: '1rem', color: '#1d1d1f', fontWeight: 800 }}>Ready to transform your marketing?</h2>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem', maxWidth: '500px', color: '#6e6e73', lineHeight: '1.6' }}>
              From scraping competitors to rendering cinematic ads and executing programmatic SEO—everything is now autonomous. Let's talk about scaling your growth.
            </p>
            <div style={{ display: 'flex', gap: '2rem', marginTop: '3rem' }}>
              <div>
                <strong style={{ display: 'block', color: '#1d1d1f', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Sales & Partnerships</strong>
                <a href="mailto:contact@eagentix.com" style={{ textDecoration: 'none', color: '#0071e3' }}>contact@eagentix.com</a>
              </div>
            </div>
          </div>
          <div style={{ flex: '1 1 500px', maxWidth: '520px', width: '100%' }}>
            <LeadForm />

            {/* P.S. Section (§3.12) */}
            <div style={{ marginTop: '32px', padding: '20px 24px', borderRadius: '16px', background: 'white', border: '1px dashed #8b5cf6', textAlign: 'center' }}>
              <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                <strong style={{ color: '#8b5cf6', fontStyle: 'normal' }}>P.S.</strong> All 28 studios operate with rights-clean commercial assets. Your brand kit design tokens are saved privately to your account.
              </p>
            </div>
          </div>
        </div>
      </section>


      {lightboxId && createPortal(
        <div className="lightbox-overlay" onClick={() => setLightboxId(null)}>
          <button className="lightbox-close" onClick={() => setLightboxId(null)}>✕</button>
          <div className="lightbox-content" style={{ maxWidth: 'min(1200px, 95vw)', background: 'transparent', boxShadow: 'none' }} onClick={(e) => e.stopPropagation()}>
            {lightboxId?.match(/\.(png|jpe?g|gif|webp)$/i) ? (
              <img
                src={`/videos/${lightboxId}`}
                alt={lightboxId}
                style={{ width: '100%', maxHeight: '95vh', borderRadius: '16px', display: 'block', objectFit: 'contain' }}
              />
            ) : (
              <video
                src={`/videos/${lightboxId}`}
                controls
                autoPlay
                playsInline
                style={{ width: '100%', maxHeight: '95vh', borderRadius: '16px', display: 'block', objectFit: 'contain' }}
              />
            )}
          </div>
        </div>,
        document.body
      )}
      <style>{`
        /* footer matches site default on marketing page */

        /* Mobile adjustments for lightbox */
        @media (max-width: 768px) {
          .lightbox-close {
            top: 10px;
            right: 15px;
            font-size: 28px;
            background: rgba(0,0,0,0.5);
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>
    </main>
  );
}
